import { Box, Typography, Paper, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useEffect, useMemo, useState } from "react";
import { getCatchesReport, getLandings, getWeightNotesReport } from "../../../Services/LondunarkerfiAPIService";
import Landing from "../../../Models/LandingModels/Landing";
import User from "../../../Models/UserModels/User";
import WeightNote from "../../../Models/WeightNoteModels/WeightNote";
import WeightNoteTypes from "../../../Constants/WeightNoteTypes";
import WeightReport from "../../../Models/ReportModels/WeightReport";

interface OverViewProps {
  user: User;
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#e57373", "#ba68c8"];

type PieDatum = { name: string; value: number };

// Small, reusable card for all pie charts with a right-side legend (prevents label cut-offs)
function PieCard({
  title,
  data,
  unit,
  maxLegendHeight = 260,
  pieOuterRadius = 85,
}: {
  title: string;
  data: PieDatum[];
  unit: string;
  maxLegendHeight?: number;
  pieOuterRadius?: number;
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const total = useMemo(() => data.reduce((s, d) => s + (d?.value ?? 0), 0), [data]);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        textAlign: "center",
        minWidth: isSmall ? 280 : 425,
        flex: isSmall ? "1 1 100%" : "1 1 340px",
        maxWidth: "100%",
        minHeight: 425,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        bgcolor: (t) => (t.palette.mode === "dark" ? "background.paper" : undefined),
      }}
    >
      <Typography variant="h6" mb={2}>{title}</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart
          margin={isSmall ? { top: 8, right: 8, bottom: 8, left: 8 } : { top: 8, right: 8, bottom: 8, left: 8 }}
        >
          <Pie
            data={data}
            labelLine={false}
            cx={isSmall ? "50%" : "38%"}
            cy="50%"
            outerRadius={isSmall ? Math.max(60, pieOuterRadius - 15) : pieOuterRadius}
            dataKey="value"
            label={false}
            isAnimationActive={false}
          >
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>

          <Tooltip
            formatter={(value: unknown, name: unknown) => {
              const v = Number(value ?? 0);
              const pct = total > 0 ? Math.round((v / total) * 100) : 0;
              return [`${v.toLocaleString("is-IS")} ${unit}`, `${String(name)} (${pct}%)`];
            }}
            contentStyle={{ border: "1px solid #888" }}
          />

          <Legend
            // ↓↓↓ on phones put legend at bottom, full width
            layout={isSmall ? "horizontal" : "vertical"}
            align={isSmall ? "center" : "right"}
            verticalAlign={isSmall ? "bottom" : "middle"}
            wrapperStyle={{
              paddingLeft: isSmall ? 0 : 12,
              maxHeight: isSmall ? undefined : maxLegendHeight,
              overflowY: isSmall ? "visible" : "auto",
              width: isSmall ? "100%" : "46%", // key change
            }}
            formatter={(value: unknown, entry: import("recharts").LegendPayload) => {
              const payload = entry?.payload as Partial<PieDatum> | undefined;
              const v = Number(payload?.value ?? 0);
              const pct = total > 0 ? Math.round((v / total) * 100) : 0;
              const formatted = v.toLocaleString("is-IS");
              return `${value} — ${formatted} (${pct}%)`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
}

export default function Overview(props: OverViewProps) {
  const [landings, setLandings] = useState<Landing[]>([]);
  const [weightNotes, setWeightNotes] = useState<WeightNote[]>([]);
  const [catchRegistration, setCatchRegistration] = useState<WeightReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [landingsData, weightNotesData, catchData] = await Promise.all([
          getLandings(undefined, true, 1),
          getWeightNotesReport(props.user.userPickedHarbour, "", 1),
          getCatchesReport(props.user.userPickedHarbour, "", 1),
        ]);
        setLandings(landingsData);
        setWeightNotes(weightNotesData);
        setCatchRegistration(catchData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [props.user.userPickedHarbour]);

  // Ship pie chart
  const prepareShipData = (): PieDatum[] => {
    const shipCounts: Record<string, number> = {};
    landings.forEach((landing) => {
      const shipName = landing.ship?.name || `Ship ${landing.ship?.shipRegistrationNumber}`;
      if (!shipName) return;
      shipCounts[shipName] = (shipCounts[shipName] || 0) + 1;
    });
    let shipData = Object.entries(shipCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    if (shipData.length > 5) {
      const topShips = shipData.slice(0, 5);
      const othersCount = shipData.slice(5).reduce((sum, ship) => sum + ship.value, 0);
      shipData = [...topShips, { name: "Önnur skip", value: othersCount }];
    }
    return shipData;
  };

  // Bar chart for landing dates
  const prepareLandingDateData = () => {
    const dateCounts: Record<string, number> = {};
    landings.forEach((landing) => {
      const dateObj = landing.landingStarts ? new Date(landing.landingStarts) : null;
      const date = dateObj
        ? `${dateObj.getDate().toString().padStart(2, "0")}.${(dateObj.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.${dateObj.getFullYear()}`
        : "Óþekkt";
      dateCounts[date] = (dateCounts[date] || 0) + 1;
    });
    return Object.entries(dateCounts)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => {
        if (a.date === "Óþekkt") return -1;
        if (b.date === "Óþekkt") return 1;
        const [dayA, monthA, yearA] = a.date.split(".");
        const [dayB, monthB, yearB] = b.date.split(".");
        const dateA = new Date(+yearA, +monthA - 1, +dayA);
        const dateB = new Date(+yearB, +monthB - 1, +dayB);
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Pie chart for weightNote types
  const prepareWeightNoteTypeData = (): PieDatum[] => {
    const toReweighting = WeightNoteTypes.TO_REWEIGHING;
    const fromReWeighting = WeightNoteTypes.FROM_REWEIGHTER;
    const typeCounts: Record<string, number> = {};
    weightNotes.forEach((note) => {
      const typeId = note.weightNoteType?.id;
      let typeName = note.weightNoteType?.name || "Óþekkt";
      if (typeId === toReweighting || typeId === fromReWeighting) {
        typeName = "Endurvigtun";
      }
      typeCounts[typeName] = (typeCounts[typeName] || 0) + 1;
    });
    return Object.entries(typeCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Pie chart for receivers (treat TO/FROM reweighing as one)
  const prepareReceiverData = (): PieDatum[] => {
    const toReweighting = WeightNoteTypes.TO_REWEIGHING;
    const fromReWeighting = WeightNoteTypes.FROM_REWEIGHTER;
    const receiverCounts: Record<string, number> = {};
    const countedNotes = new Set<number>();

    weightNotes.forEach((note) => {
      const receiverName = note.receiver?.name || "Óþekkt";
      const typeId = note.weightNoteType?.id;
      if ((typeId === toReweighting || typeId === fromReWeighting) && !countedNotes.has(note.id)) {
        const pairedNote = weightNotes.find(
          (n) =>
            n.id !== note.id &&
            n.receiver?.name === receiverName &&
            (n.weightNoteType?.id === toReweighting || n.weightNoteType?.id === fromReWeighting)
        );
        if (pairedNote) {
          countedNotes.add(note.id);
          countedNotes.add(pairedNote.id);
          receiverCounts[receiverName] = (receiverCounts[receiverName] || 0) + 1;
          return;
        }
      }
      if (!countedNotes.has(note.id)) {
        countedNotes.add(note.id);
        receiverCounts[receiverName] = (receiverCounts[receiverName] || 0) + 1;
      }
    });

    let receiverData = Object.entries(receiverCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const maxReceivers = 5;
    if (receiverData.length > maxReceivers) {
      const topReceivers = receiverData.slice(0, maxReceivers);
      const othersCount = receiverData.slice(maxReceivers).reduce((sum, r) => sum + r.value, 0);
      receiverData = [...topReceivers, { name: "Aðrir viðtakendur", value: othersCount }];
    }
    return receiverData;
  };

  // Fish species (group extras into "Aðrar tegundir")
  const prepareFishData = (): PieDatum[] => {
    const fishCounts: Record<string, number> = {};
    catchRegistration.forEach((item) => {
      const fishName = item.fishingStock?.name || "Óþekkt";
      fishCounts[fishName] = (fishCounts[fishName] || 0) + 1;
    });
    let fishData = Object.entries(fishCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const maxFish = 5;
    if (fishData.length > maxFish) {
      const topFish = fishData.slice(0, maxFish);
      const othersCount = fishData.slice(maxFish).reduce((sum, fish) => sum + fish.value, 0);
      fishData = [...topFish, { name: "Aðrar tegundir", value: othersCount }];
    }
    return fishData;
  };

  // Destinies (group extras)
  const prepareDestinyData = (): PieDatum[] => {
    const destinyCounts: Record<string, number> = {};
    catchRegistration.forEach((item) => {
      const destinyName = item.destiny?.name || "Óþekkt";
      destinyCounts[destinyName] = (destinyCounts[destinyName] || 0) + 1;
    });
    let destinyData = Object.entries(destinyCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const maxDestinies = 5;
    if (destinyData.length > maxDestinies) {
      const topDestinies = destinyData.slice(0, maxDestinies);
      const othersCount = destinyData.slice(maxDestinies).reduce((sum, d) => sum + d.value, 0);
      destinyData = [...topDestinies, { name: "Önnur afdrif", value: othersCount }];
    }
    return destinyData;
  };

  // Conditions (group extras)
  const prepareConditionData = (): PieDatum[] => {
    const conditionCounts: Record<string, number> = {};
    catchRegistration.forEach((item) => {
      const conditionName = item.condition?.name || "Óþekkt";
      conditionCounts[conditionName] = (conditionCounts[conditionName] || 0) + 1;
    });
    let conditionData = Object.entries(conditionCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const maxConditions = 5;
    if (conditionData.length > maxConditions) {
      const topConditions = conditionData.slice(0, maxConditions);
      const othersCount = conditionData.slice(maxConditions).reduce((sum, c) => sum + c.value, 0);
      conditionData = [...topConditions, { name: "Önnur ástönd", value: othersCount }];
    }
    return conditionData;
  };

  const shipData = useMemo(prepareShipData, [landings]);
  const landingDateData = useMemo(prepareLandingDateData, [landings]);
  const weightNoteTypeData = useMemo(prepareWeightNoteTypeData, [weightNotes]);
  const receiverData = useMemo(prepareReceiverData, [weightNotes]);
  const fishData = useMemo(prepareFishData, [catchRegistration]);
  const destinyData = useMemo(prepareDestinyData, [catchRegistration]);
  const conditionData = useMemo(prepareConditionData, [catchRegistration]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 500 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #232526 0%, #414345 100%)"
            : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        borderRadius: 3,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(20,20,40,0.32)"
            : "0 8px 32px rgba(60,60,100,0.12)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          justifyContent: "center",
          alignItems: "stretch",
          width: "100%",
        }}
      >
        {/* Ship Pie Chart */}
        <PieCard title="Skipting skipa" data={shipData} unit="landanir" />

        {/* WeightNote Type Pie Chart */}
        <PieCard title="Tegundir vigtarnóta" data={weightNoteTypeData} unit="vigtarnótur" />

        {/* Receiver Pie Chart */}
        <PieCard title="Viðtakendur vigtarnóta" data={receiverData} unit="vigtarnótur" />

        {/* Fish Species Pie Chart */}
        <PieCard title="Aflategundir" data={fishData} unit="vigtanir" />

        {/* Destiny Pie Chart */}
        <PieCard title="Afdrif afla" data={destinyData} unit="vigtanir" />

        {/* Condition Pie Chart */}
        <PieCard title="Ástand afla" data={conditionData} unit="vigtanir" />
      </Box>

      {/* Bar Chart for landing dates */}
      <Paper
        elevation={3}
        sx={(theme) => ({
          p: 3,
          borderRadius: 3,
          textAlign: "center",
          flex: "1 1 100%",
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : undefined,
        })}
      >
        <Typography variant="h6" mb={2}>
          {`Landanir eftir dagsetningu (${landingDateData.length} dagar)`}
        </Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={landingDateData} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
            <XAxis
              dataKey="date"
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 12, fill: "currentColor" }}
            />
            <YAxis
              label={{
                value: "Fjöldi landana",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "currentColor" },
              }}
              tick={{ fill: "currentColor" }}
            />
            <Tooltip
              formatter={(value: unknown) => [`${value} landanir`, "Fjöldi"]}
              contentStyle={{ border: "1px solid #888" }}
            />
            <Legend />
            <Bar dataKey="count" name="Fjöldi landana" fill="#0088FE" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Box>
  );
}
